import { Component, lazy, Suspense, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { publicImageUrl } from '../../lib/media';
import './productPhotoViewer.css';
import { keyed } from '../../lib/listKeys';

class ViewerBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (!this.state.failed) return this.props.children;
    return <div className="photo-viewer-message photo-viewer-bundle-loading" role="alert" data-testid="product-photo-viewer-unavailable">
      <p>The photo viewer couldn’t be loaded.</p>
      <Button variant="outline" onClick={this.props.onRetry} data-testid="product-photo-viewer-reload">Retry</Button>
    </div>;
  }
}

export const ProductPhotoViewer = ({ name, description, photos }) => {
  const [attempt, setAttempt] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const gallery = photos?.length ? photos : [''];
  const activeSrc = publicImageUrl(gallery[Math.min(activeIndex, gallery.length - 1)]);
  // A new lazy instance permits a real retry if its network chunk failed.
  const ZoomPhoto = useMemo(() => {
    void attempt;
    return lazy(() => import('./ProductPhotoZoom').then(module => ({ default: module.ProductPhotoZoom })));
  }, [attempt]);
  return (
  <DialogContent className="otc-photo-viewer" data-testid="product-photo-viewer"
    closeProps={{ 'data-testid': 'product-photo-viewer-close', 'aria-label': 'Close product photo', title: 'Close photo' }}
    onInteractOutside={event => event.preventDefault()}>
    <header className="photo-viewer-header">
      <DialogTitle className="photo-viewer-title" data-testid="product-photo-viewer-title">{name}</DialogTitle>
      <DialogDescription className="sr-only">Product photo viewer</DialogDescription>
    </header>
    {description && <p className="photo-viewer-description" data-testid="product-photo-viewer-description">{description}</p>}
    {gallery.length > 1 && (
      <div className="photo-viewer-thumbs" role="tablist" aria-label="Product photos" data-testid="product-photo-viewer-thumbs">
        {keyed(gallery).map(({ item: url, key }, i) => (
          <button key={key} type="button" role="tab" aria-selected={i === activeIndex} aria-current={i === activeIndex}
            className="photo-viewer-thumb" onClick={() => setActiveIndex(i)} data-testid={`product-photo-viewer-thumb-${i}`}>
            <img src={publicImageUrl(url)} alt={`${name} photo ${i + 1}`} loading="lazy" />
          </button>
        ))}
      </div>
    )}
    <div className="photo-viewer-body">
      <ViewerBoundary key={attempt} onRetry={() => setAttempt(n => n + 1)}>
        <Suspense fallback={<div className="photo-viewer-message photo-viewer-bundle-loading" role="status" data-testid="product-photo-viewer-loading"><Loader2 className="animate-spin" aria-hidden="true" /><span>Loading photo…</span></div>}>
          <ZoomPhoto key={activeSrc} name={name} src={activeSrc} />
        </Suspense>
      </ViewerBoundary>
    </div>
  </DialogContent>
  );
};